// START IMPORTS
import { peopleTypes } from '@ltvco/refresh-lib/v1';
import {
  AssessedValue,
  BuildingPermits as BuildingPermitsV2,
  DeedsSection,
  HomeOwnerAssociationSection,
  KnowYourNeighborhood,
  MarketInsightsSection,
  NaturalHazards,
  NotesSection,
  PdfSection,
  PropertyDemographicsSection,
  PropertyLiensSection,
  PropertyLotBuildingSection,
  PropertyOwnersSection,
  PropertyReportOverview,
  PropertySchools,
  PropertySexOffendersSection,
  PropertyValuationSection,
  RelatedReportsSection,
  ReportRating,
  ResidentsSection,
  PerksBannerSection,
  PropertySmartInsightsContainer,
} from '@ltvco/refresh-lib/v2';

import { PropertyReportBusinessPromoRow } from '../PropertyReportBusinessPromoRow';
import { NeighborhoodInsightsBanner } from '../propertyReportPromo/NeighborhoodInsightsBanner';
import {
  OverviewInsightsWrap,
  PropertyProfessionalInsights,
} from '../propertyReportPromo/PropertyProfessionalInsights';
import { PropertyReportProfessionalTools } from '../propertyReportPromo/PropertyReportProfessionalTools';
import { RealEstateViewIntroBanner } from '../propertyReportPromo/RealEstateViewIntroBanner';

import type { PropertySectionKey } from './propertySectionKeys';
import type { ComponentProps, ReactNode } from 'react';
// END IMPORTS

// START INTERFACES_AND_TYPES
type OverviewProps = ComponentProps<typeof PropertyReportOverview>;
type ValuationProps = ComponentProps<typeof PropertyValuationSection>;
type MarketInsightsProps = ComponentProps<typeof MarketInsightsSection>;

/**
 * Data and callbacks a `PropertyReportSectionRenderer` needs in order to
 * render any section of the property report. Built once per render by
 * `PropertyReportPage` and passed to every section in the active order.
 */
export interface PropertyReportSectionContext {
  /** Serialized property entity for the current report */
  property: OverviewProps['propertyReportData'];
  /** Full serialized report payload (used by a few sections instead of `property`) */
  reportData: NonNullable<OverviewProps['serializedPropertyReportData']>;
  permalink: string;
  displayAddress: string;
  marketValues: NonNullable<OverviewProps['marketValues']>;
  rentalValues: ValuationProps['rentalValues'];
  avmsPartners: ValuationProps['avmsPartners'];
  updatedBuilding: OverviewProps['updatedBuilding'];
  userCode: string;
  reportType: string;
  marketInsightsTabs: MarketInsightsProps['tabs'];
  isInPropertySmartInsightsVariation: boolean;
  isInTaxHistoryVariation: boolean;
  onEditProperty: () => void;
  onExploreNeighborhoodTab: () => void;
  onExploreLocalCrime: () => void;
  onViewRealEstateNow: () => void;
  preforeclosures: ComponentProps<
    typeof PropertyProfessionalInsights
  >['preforeclosures'];
  preforeclosuresSettled: boolean;
}

export type PropertyReportSectionRenderer = (
  ctx: PropertyReportSectionContext
) => ReactNode;
// END INTERFACES_AND_TYPES

// START CONSTANTS
/**
 * Keyed lookup of every renderable property report section. Each entry
 * owns any feature-flag gating that previously lived inline in
 * `PropertyReport.tsx`, so callers only need to iterate a section order
 * (see `usePropertyReportSectionOrder`) and render whatever key comes next.
 */
export const PROPERTY_REPORT_SECTION_REGISTRY: Record<
  PropertySectionKey,
  PropertyReportSectionRenderer
> = {
  overview: (ctx) => (
    <OverviewInsightsWrap>
      <PropertyReportOverview
        propertyReportData={ctx.property}
        serializedPropertyReportData={ctx.reportData}
        permalink={ctx.permalink}
        updatedBuilding={ctx.updatedBuilding}
        marketValues={ctx.marketValues}
        allowClaiming={false}
      />
      <PropertyProfessionalInsights
        property={ctx.property}
        permalink={ctx.permalink}
        preforeclosures={ctx.preforeclosures}
        preforeclosuresSettled={ctx.preforeclosuresSettled}
        reportUpdatedAt={ctx.reportData.meta?.updated_at ?? null}
      />
    </OverviewInsightsWrap>
  ),
  neighborhood_insights_banner: (ctx) => (
    <NeighborhoodInsightsBanner onExploreClick={ctx.onExploreNeighborhoodTab} />
  ),
  professional_tools: () => <PropertyReportProfessionalTools />,
  business_promo_row: (ctx) => (
    <PropertyReportBusinessPromoRow
      userCode={ctx.userCode || ''}
      permalink={ctx.permalink}
      reportType={ctx.reportType}
      parcelAddress={ctx.property.parcel_address || null}
    />
  ),
  property_smart_insights: (ctx) =>
    ctx.isInPropertySmartInsightsVariation ? (
      <PropertySmartInsightsContainer reportData={ctx.reportData} />
    ) : null,
  property_details: (ctx) => (
    <PropertyLotBuildingSection
      propertyAddress={ctx.displayAddress}
      property={ctx.property}
      updatedBuilding={ctx.updatedBuilding}
      canEditProperty={true}
      setIsEditModalOpen={ctx.onEditProperty}
    />
  ),
  real_estate_view_intro: (ctx) => (
    <RealEstateViewIntroBanner onViewNow={ctx.onViewRealEstateNow} />
  ),
  assessed_value_and_taxes: (ctx) => (
    <AssessedValue
      property={ctx.property}
      useTaxHistory={ctx.isInTaxHistoryVariation}
    />
  ),
  know_your_neighborhood: (ctx) => (
    <KnowYourNeighborhood
      propertyAddress={ctx.displayAddress}
      latitude={ctx.property.latitude}
      longitude={ctx.property.longitude}
    />
  ),
  building_permits: (ctx) => (
    <BuildingPermitsV2
      propertyAddress={ctx.displayAddress}
      property={ctx.property}
      permalink={ctx.permalink}
    />
  ),
  home_owner_association: (ctx) => (
    <HomeOwnerAssociationSection hoas={ctx.property.hoas} />
  ),
  property_valuation: (ctx) => (
    <PropertyValuationSection
      property={ctx.property}
      propertyAddress={ctx.displayAddress}
      marketValues={ctx.marketValues}
      rentalValues={ctx.rentalValues}
      avmsPartners={ctx.avmsPartners}
      permalink={ctx.permalink}
    />
  ),
  possible_owners: (ctx) => (
    <PropertyOwnersSection
      owners={ctx.property.owners as peopleTypes.Identities}
      residents={ctx.property.residents}
      deeds={ctx.property.deeds}
      propertyAddress={ctx.displayAddress}
      permalink={ctx.permalink}
    />
  ),
  residents: (ctx) => (
    <ResidentsSection
      residents={ctx.property.residents}
      owners={ctx.property.owners as peopleTypes.Identities}
      propertyAddress={ctx.displayAddress}
      permalink={ctx.permalink}
    />
  ),
  deeds: (ctx) => <DeedsSection deeds={ctx.property.deeds} />,
  property_liens: (ctx) => (
    <PropertyLiensSection
      fullAddress={ctx.displayAddress}
      liens={ctx.property.courts}
      permalink={ctx.permalink}
    />
  ),
  market_insights: (ctx) => (
    <MarketInsightsSection
      tabs={ctx.marketInsightsTabs.filter((item) => !!item)}
    />
  ),
  sex_offenders: (ctx) => (
    <PropertySexOffendersSection
      sexOffenders={ctx.reportData.sex_offenders}
      onExploreLocalCrime={ctx.onExploreLocalCrime}
    />
  ),
  property_demographics: (ctx) => (
    <PropertyDemographicsSection
      property={ctx.property}
      propertyAddress={ctx.displayAddress}
    />
  ),
  property_schools: (ctx) => (
    <PropertySchools
      propertyAddress={ctx.displayAddress}
      property={ctx.reportData}
    />
  ),
  natural_hazards: (ctx) => <NaturalHazards property={ctx.property} />,
  perks_banner: (ctx) => <PerksBannerSection permalink={ctx.permalink} />,
  pdf: (ctx) => <PdfSection permalink={ctx.permalink} />,
  notes: (ctx) => <NotesSection permalink={ctx.permalink} />,
  related_reports: (ctx) => (
    <RelatedReportsSection
      reportType="property"
      personName={ctx.displayAddress}
      potentialOwners={ctx.property.owners}
      relatives={[]}
    />
  ),
  report_rating: (ctx) => (
    <ReportRating
      rating={ctx.reportData.meta?.rating || null}
      report_type="property"
    />
  ),
};
// END CONSTANTS
